import { pageInfoFragment } from "@saleor/fragments/pageInfo";
import makeBossQuery from "@saleor/hooks/makeBossQuery";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import {
  InitialFinanceOrderFilterData,
  InitialFinanceOrderFilterDataVariables
} from "./types/InitialFinanceOrderFilterData";
import {
  PayoutBatchTask,
  PayoutBatchTaskVariables
} from "./types/PayoutBatchTask";
import {
  PayoutFinanceAdjustment,
  PayoutFinanceAdjustmentVariables
} from "./types/PayoutFinanceAdjustment";
import { PayoutMalls, PayoutMallsVariables } from "./types/PayoutMalls";
import { PayoutOrders, PayoutOrdersVariables } from "./types/PayoutOrders";
import {
  PayoutOrdersDownload,
  PayoutOrdersDownloadVariables
} from "./types/PayoutOrdersDownload";

export const PayoutOrderList = gql`
  ${pageInfoFragment}
  query PayoutOrders(
    $before: String
    $after: String
    $first: Int
    $last: Int
    $filter: StoreOrdersBiFilterInput!
  ) {
    storeOrdersPayout(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        cursor
        node {
          id
          mall_name
          brand_name
          vendor_code
          order_date
          completed_at
          store_order_code
          retail_price
          sales_discount_tenant
          sales_discount_sm
          sales_discount_total
          selling_price
          delivery_fee
          total_gross_transaction
          payout_status
          shipping_cost
          rebate_amount
          voucher_discount_tenant
          voucher_discount_sm
          voucher_discount_total
          price_paid
          payment_fee
          payment_fee_vat
          payment_fee_adjustment
          payment_fee_adjustment_vat
          total_payment_fee
          payment_fee_witholding_tax
          payment_method_type
          credit_card_witholding_tax
          other_adjustment
          other_remarks
          in_store_cash_payment
          net_settlement
          settlement_date
          mdr
          payment_gateway
          cash_handling_fee
          insurance
          transaction_income
          payment_fee_2307
          legal_name
          order_status
          order_code
          penalties
          delivery_fee_adjustment
          store_order_status
        }
      }
    }
  }
`;

export const usePayoutOrderList = makeBossQuery<
  PayoutOrders,
  PayoutOrdersVariables
>(PayoutOrderList);

export const initialFinanceOrderFilterDataQuery = gql`
  query InitialFinanceOrderFilterData($brand: [ID]) {
    brands(first: 5, filter: { id: $brand }) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const useInitialFinanceOrderFilterDataQuery = makeQuery<
  InitialFinanceOrderFilterData,
  InitialFinanceOrderFilterDataVariables
>(initialFinanceOrderFilterDataQuery);

export const PayoutBatchTaskQuery = gql`
  query PayoutBatchTask($id: String!) {
    payoutBatchTask(id: $id) {
      id
      status
      params
      started_at
      ended_at
      batchErrors {
        field
        message
        code
      }
    }
  }
`;

export const usePayoutBatchTaskQuery = makeBossQuery<
  PayoutBatchTask,
  PayoutBatchTaskVariables
>(PayoutBatchTaskQuery);

const PayoutOrderListDownload = gql`
  ${pageInfoFragment}
  query PayoutOrdersDownload(
    $before: String
    $after: String
    $first: Int
    $last: Int
    $filter: StoreOrdersBiFilterInput!
  ) {
    storeOrdersPayout(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        cursor
        node {
          id
          mall_name
          legal_name
          brand_name
          vendor_code
          order_date
          completed_at
          store_order_code
          retail_price
          sales_discount_tenant
          sales_discount_sm
          sales_discount_total
          selling_price
          delivery_fee
          total_gross_transaction
          voucher_discount_tenant
          voucher_discount_sm
          voucher_discount_total
          price_paid
          payment_fee
          payment_fee_vat
          payment_method_type
          payment_fee_adjustment
          payment_fee_adjustment_vat
          other_adjustment
          other_remarks
          penalties
          delivery_fee_adjustment
          total_payment_fee
          payment_fee_witholding_tax
          credit_card_witholding_tax
          in_store_cash_payment
          net_settlement
          settlement_date
          payout_status
          shipping_cost
          rebate_amount
          insurance
          transaction_income
          payment_fee_2307
          mdr
          payment_gateway
          cash_handling_fee
        }
      }
    }
  }
`;

export const usePayoutOrderListDownloadQuery = makeBossQuery<
  PayoutOrdersDownload,
  PayoutOrdersDownloadVariables
>(PayoutOrderListDownload);

export const PayoutMallsQuery = gql`
  query PayoutMalls(
    $before: String
    $after: String
    $first: Int
    $last: Int
    $filter: PayoutMallFilterInput
  ) {
    payoutMalls(
      before: $before
      after: $after
      first: $first
      last: $last
      filter: $filter
    ) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

export const usePayoutMallsQuery = makeBossQuery<
  PayoutMalls,
  PayoutMallsVariables
>(PayoutMallsQuery);

export const PayoutFinanceAdjustmentQuery = gql`
  query PayoutFinanceAdjustment($store_id: ID!) {
    payoutFinanceAdjustment(store_id: $store_id) {
      id
      storeorder_id
      payment_fee_adjustment
      payment_fee_adjustment_vat
      other_adjustment
      delivery_fee_adjustment
      penalties
      other_adjustment_remarks
      settlement_status
    }
  }
`;

export const usePayoutFinanceAdjustment = makeBossQuery<
  PayoutFinanceAdjustment,
  PayoutFinanceAdjustmentVariables
>(PayoutFinanceAdjustmentQuery);
